import { TVDisplayButton } from '@/components/programming/TVDisplayButton';
import { WorkoutAttributionBadge } from '@/components/programming/WorkoutAttributionBadge';
import { WorkoutCreatorMetadata } from '@/components/programming/WorkoutCreatorMetadata';
import { Badge } from '@/components/ui/badge';
import type { WorkoutAttribution } from '@ergshoot/shared';
import { ChevronUp, Clock, Wrench } from 'lucide-react';
import type { ReactNode } from 'react';

interface WorkoutDetailProps {
  workout: {
    id: string;
    name: string;
    description?: string | null;
    movements?: string[];
    scoring_type?: string;
    scoring_direction?: string;
    equipment?: string[];
    scheduled_date?: string;
    category?: string | null;
    coach_notes?: string | null;
    duration_minutes?: number | null;
    instructions?: string | null;
    attribution?: WorkoutAttribution | null;
    created_at?: string | null;
    created_by_username?: string | null;
  };
  onClose?: () => void;
  actions?: ReactNode;
}

export function WorkoutDetail({ workout, onClose, actions }: WorkoutDetailProps) {
  // Determine what to show as the "written workout" content (monospace block).
  // Priority: instructions > description (multi-line) > joined movements
  const writtenWorkout =
    workout.instructions ||
    (workout.description?.includes('\n') ? workout.description : null) ||
    (workout.movements && workout.movements.length > 0 ? workout.movements.join('\n') : null) ||
    workout.description;

  // Show description as overview subtitle only when it's different from the written workout
  const showDescription =
    workout.description &&
    workout.description !== writtenWorkout &&
    workout.description !== workout.name;

  return (
    <div className="p-4 space-y-4 bg-card border border-border rounded-lg m-2">
      {/* 1. Title + Collapse */}
      <div className="flex items-center justify-between">
        <h4 className="text-lg font-bold text-foreground">{workout.name}</h4>
        {onClose && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <ChevronUp className="h-4 w-4" />
            Collapse
          </button>
        )}
      </div>

      {/* 2. Description (short overview) */}
      {showDescription && (
        <p className="text-sm text-muted-foreground whitespace-pre-wrap">{workout.description}</p>
      )}

      {/* 3. Lozenges / Metadata — no scoring_direction ("Ascending") */}
      <div className="flex flex-wrap gap-2">
        <Badge variant="secondary">{workout.scoring_type}</Badge>
        {workout.duration_minutes && (
          <Badge variant="outline" className="gap-1">
            <Clock className="h-3 w-3" />
            {workout.duration_minutes} min
          </Badge>
        )}
        {workout.scheduled_date && workout.scheduled_date !== '9999-12-31' && (
          <Badge variant="outline">
            {new Date(`${workout.scheduled_date}T00:00:00`).toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
            })}
          </Badge>
        )}
        {workout.category && <Badge variant="outline">{workout.category}</Badge>}
        <WorkoutAttributionBadge attribution={workout.attribution} />
      </div>

      <WorkoutCreatorMetadata
        attribution={workout.attribution}
        created_at={workout.created_at}
        created_by_username={workout.created_by_username}
      />

      {/* 4. Equipment */}
      {workout.equipment && workout.equipment.length > 0 && (
        <div className="space-y-1">
          <span className="text-xs font-semibold text-primary uppercase tracking-wide flex items-center gap-1">
            <Wrench className="h-3 w-3" />
            Equipment
          </span>
          <div className="flex flex-wrap gap-1.5">
            {workout.equipment.map((e) => (
              <Badge key={e} variant="outline" className="text-xs">
                {e}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* 5. Written Workout (the actual session in monospace) */}
      {writtenWorkout ? (
        <div className="space-y-1">
          <span className="text-xs font-semibold text-primary uppercase tracking-wide">
            Workout
          </span>
          <pre className="whitespace-pre-wrap font-mono text-sm text-foreground bg-muted/50 border border-border rounded-md p-3">
            {writtenWorkout}
          </pre>
        </div>
      ) : (
        workout.description &&
        !showDescription && (
          <div className="space-y-1">
            <span className="text-xs font-semibold text-primary uppercase tracking-wide">
              Workout
            </span>
            <pre className="whitespace-pre-wrap font-mono text-sm text-foreground bg-muted/50 border border-border rounded-md p-3">
              {workout.description}
            </pre>
          </div>
        )
      )}

      {/* 6. Coach's Notes */}
      {workout.coach_notes && (
        <div className="space-y-1">
          <span className="text-xs font-semibold text-primary uppercase tracking-wide">
            Coach&apos;s Notes
          </span>
          <p className="text-sm italic text-foreground/80">{workout.coach_notes}</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
        <TVDisplayButton workoutId={workout.id} />
        {actions}
      </div>
    </div>
  );
}
